/**
 * Base de conhecimento de tecnologias/stacks do parque da Masterboi.
 *
 * Serve o campo "Tecnologias / Stacks" do cadastro de aplicação: o usuário
 * escolhe de uma lista agrupada por categoria (em vez de digitar do zero),
 * mas segue livre para adicionar uma tecnologia que não esteja aqui — a
 * coluna `stacks` do Postgres é texto livre.
 *
 * Os nomes são canônicos: é exatamente assim que a tecnologia deve ser
 * gravada no banco e é a chave usada em `STACK_COLORS` (catalog-config.ts)
 * para o pontinho colorido do chip.
 */

export interface StackCategory {
  id: string;
  label: string;
  items: string[];
}

// Ordem = relevância para a empresa (Power Platform e o parque Microsoft
// primeiro, depois a stack web moderna, e por fim dados e infra).
export const STACK_CATALOG: StackCategory[] = [
  {
    id: 'microsoft',
    label: 'Power Platform e Microsoft',
    items: [
      'Power Apps',
      'Power Automate',
      'Power Fx',
      'Power Pages',
      'SharePoint',
      'Dataverse',
      'Microsoft 365',
      'Microsoft Forms',
      'Excel',
      'Teams',
      'Entra ID',
      'Azure',
    ],
  },
  {
    id: 'frontend',
    label: 'Frontend',
    items: [
      'React',
      'Next.js',
      'TypeScript',
      'JavaScript',
      'Vite',
      'Tailwind',
      'shadcn/ui',
      'TanStack Query',
      'Zustand',
      'React Router',
      'Vue',
      'Angular',
      'Svelte',
    ],
  },
  {
    id: 'backend',
    label: 'Backend',
    items: [
      'Bun',
      'Hono',
      'Node.js',
      'Drizzle ORM',
      'Zod',
      'Clerk',
      'Python',
      'FastAPI',
      '.NET',
      'C#',
      'Java',
      'PHP',
      'Laravel',
      'ADVPL',
    ],
  },
  {
    id: 'mobile',
    label: 'Mobile',
    items: ['Flutter', 'Dart', 'React Native', 'Expo', 'Kotlin', 'Swift', 'PWA'],
  },
  {
    id: 'database',
    label: 'Banco de dados',
    items: ['SQL Server', 'Postgres', 'Oracle', 'MySQL', 'SQLite', 'Redis', 'MongoDB'],
  },
  {
    id: 'integracao',
    label: 'Integração e automação',
    items: [
      'n8n',
      'TOTVS Protheus',
      'SAP',
      'WMS',
      'REST API',
      'SOAP',
      'Webhooks',
      'RabbitMQ',
      'BullMQ',
      'Power Automate Desktop',
      'Selenium',
      'Playwright',
    ],
  },
  {
    id: 'dados-bi',
    label: 'Dados e BI',
    items: ['Power BI', 'Power Query', 'DAX', 'SQL', 'Pandas', 'Metabase', 'Airflow'],
  },
  {
    id: 'infra',
    label: 'Infraestrutura e DevOps',
    items: [
      'Docker',
      'Portainer',
      'Traefik',
      'Nginx',
      'MinIO',
      'Kubernetes',
      'Git',
      'GitHub Actions',
      'SonarQube',
      'Grafana',
      'Zabbix',
      'Uptime Kuma',
      'Linux',
      'Windows Server',
    ],
  },
];

// Lista achatada e sem duplicatas, na ordem das categorias.
export const ALL_STACKS: string[] = [
  ...new Set(STACK_CATALOG.flatMap((category) => category.items)),
];

const DIACRITIC = /\p{Diacritic}/gu;

/** Minúsculas sem acento — para busca tolerante a "integracao" vs "integração". */
function normalizeStack(value: string): string {
  return value.normalize('NFD').replace(DIACRITIC, '').toLowerCase().trim();
}

// name normalizado -> label da categoria.
const CATEGORY_BY_STACK = new Map<string, string>(
  STACK_CATALOG.flatMap((category) =>
    category.items.map((item) => [normalizeStack(item), category.label] as const),
  ),
);

/**
 * Sugestões para o autocomplete: busca por substring, ignorando caixa e
 * acentos. Quem casa no início do nome vem primeiro; dentro de cada grupo,
 * mantém a ordem de relevância do catálogo.
 */
export function searchStacks(query: string, limit = 12): string[] {
  const term = normalizeStack(query);
  if (!term) return ALL_STACKS.slice(0, limit);

  const startsWith: string[] = [];
  const contains: string[] = [];
  for (const name of ALL_STACKS) {
    const at = normalizeStack(name).indexOf(term);
    if (at === 0) startsWith.push(name);
    else if (at > 0) contains.push(name);
  }
  return [...startsWith, ...contains].slice(0, limit);
}

/** Label da categoria de uma tecnologia, ou null se ela não está no catálogo. */
export function stackCategoryOf(name: string): string | null {
  return CATEGORY_BY_STACK.get(normalizeStack(name)) ?? null;
}
